import React, { Component } from 'react';
import { clear } from '../utils/storageHelper';

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, isRecovering: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo?.componentStack);

        if (error instanceof SyntaxError ||
            (error?.message && error.message.includes('Unexpected token'))) {
            console.error('Detected syntax error - likely HTML parsed as JavaScript.');
            this.handleAutoRecovery();
        }
    }

    handleAutoRecovery = async () => {
        this.setState({ isRecovering: true });

        try {
            try {
                const { attemptReauthentication } = await import('../utils/reauthenticate');
                const reauthUser = await attemptReauthentication();

                if (!reauthUser) {
                    await clear();
                    sessionStorage.clear();
                    localStorage.clear();
                }
            } catch (reauthError) {
                console.error('Reauthentication error:', reauthError);
                await clear();
                sessionStorage.clear();
                localStorage.clear();
            }

            if ('serviceWorker' in navigator) {
                const registrations = await navigator.serviceWorker.getRegistrations();
                for (let registration of registrations) {
                    await registration.unregister();
                }
            }

            if ('caches' in window) {
                const cacheNames = await caches.keys();
                await Promise.all(cacheNames.map(name => caches.delete(name)));
            }

            setTimeout(() => { window.location.reload(true); }, 1000);
        } catch (cleanupError) {
            console.error('Error during cleanup:', cleanupError);
            setTimeout(() => { window.location.reload(true); }, 1000);
        }
    };

    handleManualReset = async () => {
        try {
            await clear();
            sessionStorage.clear();
            localStorage.clear();

            if ('serviceWorker' in navigator) {
                const registrations = await navigator.serviceWorker.getRegistrations();
                for (let registration of registrations) {
                    await registration.unregister();
                }
            }

            if ('caches' in window) {
                const cacheNames = await caches.keys();
                await Promise.all(cacheNames.map(name => caches.delete(name)));
            }

            window.location.href = '/Welcome';
        } catch (resetError) {
            console.error('Error resetting app:', resetError);
            window.location.reload(true);
        }
    };

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        if (this.state.isRecovering) {
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    padding: '20px',
                    textAlign: 'center'
                }}>
                    <h1>Recovering from error...</h1>
                    <p>Clearing cache and reloading the application.</p>
                    <p>Please wait...</p>
                </div>
            );
        }

        const { error } = this.state;

        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                padding: '20px',
                textAlign: 'center',
                fontFamily: 'system-ui, -apple-system, sans-serif'
            }}>
                <h1 style={{ color: '#dc3545', marginBottom: '20px' }}>
                    Something went wrong
                </h1>
                <p style={{ marginBottom: '10px', maxWidth: '600px' }}>
                    The application encountered an error. This may be due to cached data from a previous session.
                </p>
                {error && (
                    <details style={{
                        marginTop: '20px',
                        padding: '10px',
                        background: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        maxWidth: '600px',
                        width: '100%',
                        textAlign: 'left'
                    }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                            Error details
                        </summary>
                        <pre style={{
                            marginTop: '10px',
                            fontSize: '12px',
                            overflow: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                        }}>
                            {error.toString()}
                        </pre>
                    </details>
                )}
                <button
                    onClick={this.handleManualReset}
                    style={{
                        marginTop: '30px',
                        padding: '12px 24px',
                        fontSize: '16px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: '500'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
                >
                    Clear Cache and Reload
                </button>
            </div>
        );
    }
}

export default ErrorBoundary;
